export enum MultiFactorAuthStatus {
    MFASTATUS_INIT = "init",
    MFASTATUS_ENABLED = "enabled",
    MFASTATUS_DISABLED = "disabled"
}

export enum MultiFactorAuthType {
    MFATYPE_TOTP = "totp"
}

/**
 * @enum {string}
 * @public
 * @readonly
 * @description
 *      Multifactor authentication (MFA) policy.
 */
export enum MultiFactorAuthPolicy {
    /**
     * @public
     * @event MultiFactorAuthPolicy#MFAPOLICY_NEVER
     * @description
     *      no Multi Factor requested to the user
     */
    MFAPOLICY_NEVER = "never",

    /**
     * @public
     * @event MultiFactorAuthPolicy#MFAPOLICY_UNTRUSTED_ONLY
     * @description
     *      Multi Factor is requested to the user only for untrusted applications and browsers (user has the possibility to trust applications/browsers so that the multi factor is no longer requested for this application/browser
     */
    MFAPOLICY_UNTRUSTED_ONLY = "untrusted_only",

    /**
     * @public
     * @event MultiFactorAuthPolicy#MFAPOLICY_ALWAYS
     * @description
     *      Multi Factor is always requested to the user
     */
    MFAPOLICY_ALWAYS = "always"
}

export class MultiFactorAuthInformation {
    /**
     * Type of multifactor
     *
     * @type {MultiFactorAuthType}
     * @memberof MultiFactorAuthInformation
     */
    public mfaType: MultiFactorAuthType;

    /**
     * secret of TOTP multifactor
     *
     * @type {string}
     * @memberof MultiFactorAuthInformation
     */
    public mfaSecret: string;

    /**
     * 	OTP auth url computed from secret (see https://github.com/google/google-authenticator/wiki/Key-Uri-Format)
     *
     * @type {string}
     * @memberof MultiFactorAuthInformation
     */
    public otpAuthUrl: string;


    /**
     * QR code generated from OTP Url
     *
     * @type {string}
     * @memberof MultiFactorAuthInformation
     */
    public qrcode: string;

    private constructor(
        mfaType: MultiFactorAuthType, 
        mfaSecret: string,
        otpAuthUrl: string, 
        qrcode: string
    ) {
        this.mfaType = mfaType;
        this.mfaSecret = mfaSecret;
        this.otpAuthUrl = otpAuthUrl;
        this.qrcode = qrcode;
    }    

    public static createMFAInfoFromData(data: any): MultiFactorAuthInformation {
        const mfaInfo = new MultiFactorAuthInformation(
            data.mfaType ?? MultiFactorAuthType.MFATYPE_TOTP,
            data.mfaSecret ?? '',
            data.otpAuthUrl ?? '',
            data.qrcode ?? ''
        );
        return mfaInfo;
    }
    
}

export class MultiFactorAuthRainbowAuth {
    /**
     * status of the activation of the rainbow policy
     *
     * @type {MultiFactorAuthStatus}
     * @memberof MultiFactorAuthRainbowAuth
     */
    public mfaStatus: MultiFactorAuthStatus;

    /**
     * list of trusted application of rainbow policy
     *
     * @type {any[]}
     * @memberof MultiFactorAuthRainbowAuth
     */
    public mfaTrustedApps: any[];

    /**
     * type of multifactor
     *
     * @type {MultiFactorAuthType}
     * @memberof MultiFactorAuthRainbowAuth
     */
    public mfaType: MultiFactorAuthType;

    constructor(mfaStatus: MultiFactorAuthStatus, mfaTrustedApps: any[], mfaType: MultiFactorAuthType) {
        this.mfaStatus = mfaStatus;
        this.mfaTrustedApps = mfaTrustedApps;
        this.mfaType = mfaType;
    }    

    public static createMFARainbowAuthFromData(data: any): MultiFactorAuthRainbowAuth {
        const mfaRbAuth = new MultiFactorAuthRainbowAuth(
            data.mfaStatus ?? '',
            data.mfaTrustedApps ?? [],
            data.mfaType ?? MultiFactorAuthType.MFATYPE_TOTP
        );
        return mfaRbAuth;
    }
}

export interface MultiFactorAuthVerifInfo {
    /**
     * @public
     * @property {MultiFactorAuthType} type of multifactor
     */
    mfaType: MultiFactorAuthType;

    /**
     * @public
     * @property {string} recovery code used as default multifactor authentication
     */
    mfaRecoveryCode: string;
}

export class MultiFactorAuthFeatureInfo {
    /**
     * @public
     * @type {string} Name of feature
     * @memberof MultiFactorAuthFeatureInfo
     */
    public feature: string;

    /**
     * @public
     * @type {string} JsonWebToken to use for all API requests concerning a feature
     * @memberof MultiFactorAuthFeatureInfo
     */
    public token: string;

    /**
     * Specific parameters associated to multifactor authentication feature
     *
     * @public
     * @type {{ mfaRecoveryCode: string; canBeDisabled: boolean }}
     * @memberof MultiFactorAuthFeatureInfo
     */
    public mfa: { mfaRecoveryCode: string; canBeDisabled: boolean };

    constructor(feature: string, token: string, mfa: { mfaRecoveryCode: string; canBeDisabled: boolean }) {
        this.feature = feature;
        this.token = token;
        this.mfa = mfa;
    }    

}